import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { FOUNDERS } from "../data/site";
import { Reveal } from "../components/Reveal";

export default function Founders() {
  return (
    <>
      <Seo
        title="Founders — Omkaar Metals Ltd"
        siteName="Omkaar Metals Ltd"
        description="Meet the founders of Omkaar Metals Ltd — the Zambian team behind coal, copper and aluminium exports from Lusaka."
      />
      <section className="mx-auto max-w-[1400px] px-6 pb-28 pt-40 lg:px-12 lg:pt-52" data-testid="founders-page">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="font-mono-label text-xs text-copper"
        >
          The founders
        </motion.p>
        <div className="overflow-hidden">
          <motion.h1
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="font-display mt-6 text-5xl font-semibold uppercase leading-[0.95] tracking-tight text-zinc-100 sm:text-7xl"
            data-testid="founders-headline"
          >
            People you can<br /><span className="text-copper">shake hands with.</span>
          </motion.h1>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-8 max-w-xl text-base text-zinc-400 md:text-lg"
        >
          Commodity trade runs on trust. Omkaar Metals is owned and run by two founders —
          each with a decade in the Zambian mineral trade. The same people who sign the
          contracts, inspect the cargo and answer your calls.
        </motion.p>

        <div className="mx-auto mt-20 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
          {FOUNDERS.map((f, i) => (
            <Reveal key={f.id} delay={i * 0.12}>
              <div className="group relative border border-zinc-800 bg-surface" data-testid={`founder-card-${f.id}`}>
                <div className="h-[26rem] overflow-hidden">
                  <img
                    src={f.img}
                    alt={`${f.name}, ${f.role} of Omkaar Metals Ltd`}
                    className="clip-corner-sm h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <p className="font-stencil text-[10px] text-copper">Founder file // {f.id}</p>
                  <h2 className="font-display mt-3 text-2xl font-semibold uppercase text-zinc-100">{f.name}</h2>
                  <p className="font-mono-label mt-1 text-[10px] text-zinc-500">{f.role}</p>
                  <p className="mt-4 text-sm leading-relaxed text-zinc-400">{f.bio}</p>
                </div>
                <span className="absolute right-4 top-4 border-2 border-copper/70 bg-black/60 px-2 py-1 font-stencil text-[10px] text-copper backdrop-blur-sm">
                  10+ YRS
                </span>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="hazard mt-24" aria-hidden="true" />

        <Reveal delay={0.2} className="mt-12">
          <div className="blueprint flex flex-col items-start justify-between gap-8 border border-zinc-800 bg-surface p-10 md:flex-row md:items-center" data-testid="founders-cta">
            <div>
              <p className="font-stencil text-[10px] text-copper">Direct line // no middlemen</p>
              <h2 className="font-display mt-3 text-2xl font-semibold uppercase text-zinc-100 lg:text-3xl">Deal directly with the owners.</h2>
              <p className="mt-3 max-w-md text-sm text-zinc-400">No call centres, no brokers. Send an enquiry and Navpreet or Khush replies personally — usually within hours.</p>
            </div>
            <a
              href="/#contact"
              data-testid="founders-contact-btn"
              className="group flex items-center gap-2 bg-copper px-7 py-4 font-mono-label text-xs text-ink transition-colors duration-300 hover:bg-copper-light"
            >
              Start a conversation
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
